/**
 * =============================================================================
 * IDS CRM Web - Reopening a completed work log to update it
 * =============================================================================
 *
 * Gareth, 13 October 2026. A completed work log sometimes needs more: not
 * enough was written, or a part was missed. The engineer who completed it asks
 * for it to be reopened, naming a team leader, manager or admin, or a group;
 * one of them reopens it (or declines, with a reason); the engineer updates it
 * and completes it again. Every step and every change is listed under the log
 * as its Changes: who, what and when.
 *
 * A reopened log keeps its outcome, so its hours stay in pay as they were.
 * The server is the authority on all of it (api services/workLogReopen.js and
 * routes/ticketWorkLogReopen.js) and its sentences are shown as they are.
 *
 * What is here:
 *  - reopenActions: which buttons a work log card offers this person;
 *  - ReopenStatusLine: where a log's reopen stands, shown on its card;
 *  - RequestReopenModal, DecideReopenModal, ReopenLogModal: the steps that
 *    each need a written reason;
 *  - UpdateWorkLogModal: the reopened log itself - notes, fault codes, the
 *    ticket's summary, times, photos, the forms filled in on it, parts missed;
 *  - WorkLogChanges: the list of who did what and when.
 *
 * Every window takes basePath, the log's own API path
 * (/api/v1/tickets/:ticketId/work-log/:logId, or /api/v1/tasks/:taskId/...
 * for a job, 14 October 2026), so the same windows serve both. On a job the
 * update window offers the job's completion summary instead of the ticket's
 * resolution summary and root cause, has no fault codes (job work logs have
 * none), and each part added names its robot when the job has several.
 * =============================================================================
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatDateTime, toLocalDateTimeInput } from '../utils/format';
import { roleLabel } from '../utils/permissions';
import { filesToAttachmentPayload, ONE_SAVE_BUDGET_BYTES } from '../utils/attachments';
import { parseFaultCodes, formatFaultCodes } from '../utils/faultCodes';
import { authedFileHref } from '../utils/files';
import { firstSpareProblem } from '../utils/spareLines';
import { newUuid } from '../hooks/useSavedLogWork';
import TemplateCheckFormRenderer from './TemplateCheckFormRenderer';
import SparePartsUsed from './SparePartsUsed';
import JobStagesCard, { ARRIVED_HOME_LABEL } from './JobStagesCard';
import { formLine } from './WorkLogFormViewer';
import './WorkLogReopen.css';

/** Who may reopen a work log, decline a request, and be sent one (as the server). */
export const REOPEN_ROLES = ['team_leader', 'manager', 'admin'];
/** Who may update someone else's reopened log (as the server). */
const MANAGER_ROLES = ['manager', 'admin'];
/** The server's minimum for every written reason. */
const MIN_REASON = 5;

export const NO_SIGNAL_MESSAGE = 'That could not be sent. Check your signal and try again.';
export const NOTHING_CHANGED_MESSAGE = 'Nothing has been changed yet.';
export const SAVED_STILL_REOPENED_MESSAGE = 'Saved. The work log is still reopened: complete it again when you have finished.';
export const COMPLETED_AGAIN_MESSAGE = 'The work log has been updated and completed again.';
export const FINISH_ORDER_MESSAGE = 'Finish time must be after the start time.';
export const SUMMARY_EMPTY_MESSAGE = 'The resolution summary cannot be left empty.';
export const JOB_SUMMARY_EMPTY_MESSAGE = 'The completion summary cannot be left empty.';
export const STORED_PHOTO_MESSAGE = 'A photo already on a form cannot be removed here.';
export const TOO_MANY_PHOTOS_MESSAGE = 'These photos add up to more than one save can carry (18 MB). Save some now, then add the rest.';
export const SITE_TIMES_HINT = 'Site work takes its times from Job Stages. Type the right time and press Set; the work log follows when you save.';

const sameId = (a, b) => Boolean(a) && Boolean(b) && String(a).toLowerCase() === String(b).toLowerCase();
const words = (value) => String(value || '').replace(/_/g, ' ');

/**
 * Which reopen buttons a work log card offers this person. The server checks
 * every one again; this only stops a button being shown that would be refused.
 *
 * @param {object} log a work log with its reopen (log.reopen, or null)
 * @param {{userId: string, role: string}} user
 * @param {{readOnly?: boolean}} [options] a read-only workspace offers nothing
 */
export function reopenActions(log, user, { readOnly = false } = {}) {
  const none = { request: false, cancel: false, decide: false, reopen: false, update: false };
  if (readOnly || !log || !user) return none;
  const reopen = log.reopen || null;
  const status = reopen ? reopen.status : null;
  const active = status === 'requested' || status === 'open';
  const mine = sameId(log.engineer_id, user.userId);
  // Nobody reopens their own log: another team leader or manager does it.
  const mayReopen = REOPEN_ROLES.includes(user.role) && !mine;
  const completed = Boolean(log.outcome);
  return {
    request: completed && mine && !active,
    cancel: status === 'requested' && sameId(reopen.requested_by, user.userId),
    decide: status === 'requested' && mayReopen,
    reopen: completed && mayReopen && !active,
    update: status === 'open' && (mine || MANAGER_ROLES.includes(user.role))
  };
}

/** One sentence saying where a log's reopen stands, or null when there is nothing to say. */
export function reopenStatusText(reopen) {
  if (!reopen) return null;
  const by = (name) => (name ? ` by ${name}` : '');
  if (reopen.status === 'requested') {
    return `Reopen requested${by(reopen.requested_by_name)} on ${formatDateTime(reopen.requested_at)}`
      + `${reopen.sent_to ? `, sent to ${reopen.sent_to}` : ''}. Reason: ${reopen.request_reason}`;
  }
  if (reopen.status === 'open') {
    return `Reopened${by(reopen.reopened_by_name)} on ${formatDateTime(reopen.reopened_at)} to be updated. `
      + `Reason: ${reopen.reopen_reason}`;
  }
  if (reopen.status === 'declined') {
    return `Reopen declined${by(reopen.decided_by_name)} on ${formatDateTime(reopen.decided_at)}. `
      + `Reason: ${reopen.decision_note}`;
  }
  if (reopen.status === 'closed') {
    return `Updated and completed again${by(reopen.reclosed_by_name)} on ${formatDateTime(reopen.reclosed_at)}.`;
  }
  return null;
}

/** Where a log's reopen stands, on its card. */
export function ReopenStatusLine({ reopen }) {
  const text = reopenStatusText(reopen);
  if (!text) return null;
  return <div className={`work-log-reopen-line ${reopen.status}`} role="status">{text}</div>;
}

async function send(authFetch, path, { method = 'POST', body } = {}) {
  let response;
  try {
    response = await authFetch(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch (err) {
    throw new Error(NO_SIGNAL_MESSAGE);
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || 'That could not be saved. Try again.');
  return data;
}

/** The engineer who asked withdraws a request nobody has acted on yet. Throws the sentence to show. */
export async function cancelReopenRequest(authFetch, basePath, reopen) {
  const data = await send(authFetch, `${basePath}/reopen-request/${reopen.reopen_id}/cancel`, { body: {} });
  return data.reopen;
}

/**
 * A small window that takes one written reason and sends it. onSubmit throws
 * the sentence to show; when it resolves the parent closes the window.
 */
function ReasonWindow({
  title, intro = null, label, placeholder = '', help = null, required = true,
  submitLabel, busyLabel, canSubmit = true, children = null, onSubmit, onClose
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const reasonOk = !required || reason.trim().length >= MIN_REASON;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(reason.trim());
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content work-log-reopen-window"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{title}</h2>
          <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="modal-error" role="alert">{error}</div>}
          {intro && <div className="modal-hint">{intro}</div>}
          {children}
          <div className="form-group">
            <label htmlFor="work_log_reopen_reason">{label}{required ? ' *' : ''}</label>
            <textarea
              id="work_log_reopen_reason"
              rows={3}
              value={reason}
              onChange={event => setReason(event.target.value)}
              placeholder={placeholder}
            />
            {help && <span className="field-help">{help}</span>}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-save" onClick={submit} disabled={saving || !reasonOk || !canSubmit}>
            {saving ? busyLabel : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The engineer who completed a log asks for it to be reopened, and chooses
 * who the request goes to.
 *
 * @param {string} props.basePath the log's API path
 * @param {Function} props.authFetch
 * @param {Function} props.onClose
 * @param {(reopen: object) => void} props.onDone
 */
export function RequestReopenModal({ basePath, authFetch, onClose, onDone }) {
  const [options, setOptions] = useState({ status: 'loading', people: [], groups: [] });
  const [sendTo, setSendTo] = useState('');

  const loadOptions = useCallback(async () => {
    setOptions({ status: 'loading', people: [], groups: [] });
    try {
      const data = await send(authFetch, `${basePath}/reopen-request/options`, { method: 'GET' });
      setOptions({
        status: 'ready',
        people: Array.isArray(data.people) ? data.people : [],
        groups: Array.isArray(data.groups) ? data.groups : []
      });
    } catch (err) {
      setOptions({ status: 'failed', people: [], groups: [] });
    }
  }, [authFetch, basePath]);

  useEffect(() => { loadOptions(); }, [loadOptions]);

  const submit = async (reason) => {
    const [kind, id] = sendTo.split(':');
    const data = await send(authFetch, `${basePath}/reopen-request`, {
      body: kind === 'group' ? { reason, send_to_group_id: id } : { reason, send_to_user_id: id }
    });
    onDone(data.reopen);
  };

  return (
    <ReasonWindow
      title="Request reopen"
      intro="Say what needs adding or correcting. A team leader, manager or admin reopens the work log, and you are told when they have. Your hours stay as they are."
      label="Why does it need reopening?"
      placeholder="e.g. I missed a part off, and the notes need the serial number"
      help="Shown to whoever reopens it, and kept with the work log."
      submitLabel="Send request"
      busyLabel="Sending..."
      canSubmit={Boolean(sendTo)}
      onSubmit={submit}
      onClose={onClose}
    >
      <div className="form-group">
        <label htmlFor="work_log_reopen_send_to">Send to *</label>
        {options.status === 'failed' ? (
          <div className="modal-error" role="alert">
            The list of people could not be read. Check your signal.{' '}
            <button type="button" className="btn-small" onClick={loadOptions}>Try again</button>
          </div>
        ) : (
          <select
            id="work_log_reopen_send_to"
            value={sendTo}
            disabled={options.status !== 'ready'}
            onChange={event => setSendTo(event.target.value)}
          >
            <option value="">{options.status === 'ready' ? 'Choose a person or a group...' : 'Reading the list...'}</option>
            {options.people.length > 0 && (
              <optgroup label="People">
                {options.people.map(person => (
                  <option key={person.user_id} value={`user:${person.user_id}`}>
                    {person.full_name} ({roleLabel(person.role)})
                  </option>
                ))}
              </optgroup>
            )}
            {options.groups.length > 0 && (
              <optgroup label="Groups">
                {options.groups.map(group => (
                  <option key={group.group_id} value={`group:${group.group_id}`}>
                    {group.name} ({group.people === 1 ? '1 person' : `${group.people} people`} who can reopen)
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        )}
        <span className="field-help">
          A team leader, manager or admin, or a group: everyone in the group who can reopen a work log is told.
        </span>
      </div>
    </ReasonWindow>
  );
}

/**
 * A team leader, manager or admin answers a request: reopen it, or decline
 * with a reason.
 *
 * @param {'approve'|'decline'} props.mode
 * @param {object} props.log
 * @param {object} props.reopen the request waiting (log.reopen)
 */
export function DecideReopenModal({ basePath, log, reopen, mode, authFetch, onClose, onDone }) {
  const approving = mode === 'approve';
  const submit = async (note) => {
    const data = await send(authFetch, `${basePath}/reopen-request/${reopen.reopen_id}/${approving ? 'approve' : 'decline'}`, {
      body: { note: note || undefined }
    });
    onDone(data.reopen);
  };
  const asked = `${reopen.requested_by_name || log.engineer_name || 'The engineer'} asked on ${formatDateTime(reopen.requested_at)}: `
    + `${reopen.request_reason}`;

  return approving ? (
    <ReasonWindow
      title="Reopen this work log"
      intro={`${asked} Reopening lets them update it. It stays completed and its hours stay in pay as they are, until they complete it again.`}
      label="Note to the engineer"
      required={false}
      placeholder="Optional"
      help={`Sent to ${log.engineer_name || 'the engineer'} with the news that it is reopened.`}
      submitLabel="Reopen work log"
      busyLabel="Reopening..."
      onSubmit={submit}
      onClose={onClose}
    />
  ) : (
    <ReasonWindow
      title="Decline this request"
      intro={asked}
      label="Why is it declined?"
      placeholder="e.g. the part is already on the other work log"
      help={`Sent to ${log.engineer_name || 'the engineer'}, and kept with the work log.`}
      submitLabel="Decline request"
      busyLabel="Declining..."
      onSubmit={submit}
      onClose={onClose}
    />
  );
}

/** A team leader, manager or admin reopens a completed log without being asked. */
export function ReopenLogModal({ basePath, log, authFetch, onClose, onDone }) {
  const submit = async (reason) => {
    const data = await send(authFetch, `${basePath}/reopen`, { body: { reason } });
    onDone(data.reopen);
  };
  const who = log.engineer_name || 'The engineer';
  return (
    <ReasonWindow
      title="Reopen this work log"
      intro={`${who} completed this as ${words(log.outcome)}. Reopening lets them update it. It stays completed and its hours stay in pay as they are, until they complete it again.`}
      label="Why does it need reopening?"
      placeholder="e.g. the notes need the part number, and a part was missed"
      help={`Sent to ${log.engineer_name || 'the engineer'} so they know what to update.`}
      submitLabel="Reopen work log"
      busyLabel="Reopening..."
      onSubmit={submit}
      onClose={onClose}
    />
  );
}

// ---------------------------------------------------------------------------
// Changes: who did what, and when
// ---------------------------------------------------------------------------

const TIME_FIELDS = new Set(['started_at', 'ended_at', 'departed_at', 'arrived_at', 'departed_site_at', 'returned_at']);
const FIELD_LABELS = {
  started_at: 'Started',
  ended_at: 'Finished',
  notes: 'Notes',
  fault_codes: 'Fault codes',
  outcome: 'Outcome',
  log_type: 'Work type',
  work_period: 'Work period',
  departed_at: 'Job started (departed)',
  arrived_at: 'Arrived at site',
  departed_site_at: 'Finished site work',
  returned_at: ARRIVED_HOME_LABEL
};

const shownValue = (field, value) => {
  if (value === null || value === undefined || String(value).trim() === '') return 'nothing';
  if (TIME_FIELDS.has(field)) return formatDateTime(value);
  if (field === 'fault_codes') return formatFaultCodes(parseFaultCodes(String(value).replace(/[[\]{}"]/g, ''))) || 'nothing';
  return String(value);
};

/** What one row of a log's Changes says was done. */
export function changeText(row) {
  if (!row) return '';
  if (!row.field_name) return row.change_summary || words(row.change_type);
  const label = FIELD_LABELS[row.field_name] || words(row.field_name);
  return `${label}: ${shownValue(row.field_name, row.old_value)} → ${shownValue(row.field_name, row.new_value)}`;
}

/**
 * The Changes of one work log, read when opened: every step of a reopen and
 * every change, oldest first, each with who and when.
 *
 * @param {string} props.basePath the log's API path
 * @param {*} [props.refreshKey] changes when the log has been saved, so an open list re-reads
 */
export function WorkLogChanges({ basePath, authFetch, refreshKey = null }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ status: 'idle', rows: [] });

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState(previous => ({ status: 'loading', rows: previous.rows }));
    (async () => {
      try {
        const data = await send(authFetch, `${basePath}/changes`, { method: 'GET' });
        if (!cancelled) setState({ status: 'ready', rows: Array.isArray(data.changes) ? data.changes : [] });
      } catch (err) {
        if (!cancelled) setState({ status: 'failed', rows: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [open, basePath, authFetch, refreshKey]);

  return (
    <div className="work-log-changes">
      <button type="button" className="btn-log-action" aria-expanded={open} onClick={() => setOpen(value => !value)}>
        {open ? 'Hide changes' : 'Changes'}
      </button>
      {open && state.status === 'loading' && state.rows.length === 0 && (
        <p className="work-log-changes-note" role="status">Reading the changes...</p>
      )}
      {open && state.status === 'failed' && (
        <p className="work-log-changes-note" role="alert">The changes could not be read. Check your signal and try again.</p>
      )}
      {open && state.status === 'ready' && state.rows.length === 0 && (
        <p className="work-log-changes-note">Nothing has been changed on this work log.</p>
      )}
      {open && state.rows.length > 0 && (
        <ol className="work-log-changes-list" aria-label="Changes to this work log">
          {state.rows.map(row => (
            <li key={row.audit_id}>
              <span className="work-log-change-who">
                {formatDateTime(row.changed_at)} · {row.changed_by_name || 'The system'}
              </span>
              <span className="work-log-change-what">{changeText(row)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The reopened log itself
// ---------------------------------------------------------------------------

const hasSections = (snapshot) => Boolean(snapshot && Array.isArray(snapshot.sections) && snapshot.sections.length > 0);

/** A form's stored photos, by the question they belong to, as the renderer shows them. */
function storedStepPhotos(attachments) {
  const bySteps = {};
  (Array.isArray(attachments) ? attachments : []).forEach(photo => {
    if (!photo || !photo.step_key || !String(photo.step_key).startsWith('template:')) return;
    if (!bySteps[photo.step_key]) bySteps[photo.step_key] = [];
    bySteps[photo.step_key].push({
      ...photo,
      stored: true,
      filename: photo.name,
      file_url: photo.url && String(photo.url).startsWith('/uploads/') ? authedFileHref(photo.url) : photo.url
    });
  });
  return bySteps;
}

const newPhotoBytes = (photos) => photos.reduce((total, photo) => total + (Number(photo && photo.size) || 0), 0);

/**
 * Update a reopened work log, and complete it again.
 *
 * What can be changed is what the server allows (PATCH basePath/update):
 * notes, fault codes, the ticket's resolution summary and root cause (on the
 * log that closed it), the times, new photos, the answers and photos of the
 * forms filled in on the log (never the van check), and parts that were
 * missed. Only what was changed is sent, so a save that changes no time
 * changes no hours.
 *
 * @param {object} props
 * @param {string} props.basePath the log's API path
 * @param {'ticket'|'job'} [props.kind] what the log is on (default a ticket)
 * @param {string} [props.ticketId] the ticket the log is on (for its Job Stages)
 * @param {string} [props.taskId] the job the log is on (for its Job Stages)
 * @param {Array<{asset_id: string, asset_ref: string}>} [props.robots] a job's robots, for the parts added
 * @param {object} props.log the work log, with log.reopen
 * @param {{userId: string, role: string}} props.user
 * @param {boolean} [props.closedTheWork] this log's outcome closed the ticket, or completed the job
 * @param {{resolution_summary: ?string, root_cause: ?string, completion_summary: ?string}} [props.summary]
 *        the ticket's resolution summary and root cause, or the job's completion summary
 * @param {object[]} [props.forms] summaries of the forms on this log (van checks are left out here)
 * @param {{items: Array, status: string}|null} [props.catalogue] the spare parts list, or null when this person cannot add parts
 * @param {string} [props.recordedParts] the parts already on the log, as one line
 * @param {Function} props.authFetch
 * @param {Function} props.onClose
 * @param {(result: {completed: boolean, changed: string[], payrollNotice: ?string, alreadyRecorded: string[]}) => void} props.onSaved
 */
export function UpdateWorkLogModal({
  basePath, kind = 'ticket', ticketId = null, taskId = null, robots = [], log, user, closedTheWork = false,
  summary = null, forms = [], catalogue = null, recordedParts = '', authFetch, onClose, onSaved
}) {
  const onJob = kind === 'job';
  const jobRobots = Array.isArray(robots) ? robots.filter(robot => robot && robot.asset_id) : [];
  const reopen = log.reopen || {};
  const isSiteWork = log.log_type === 'site_work';
  const mine = sameId(log.engineer_id, user && user.userId);

  const initial = useRef({
    notes: log.notes || '',
    faultCodes: formatFaultCodes(log.fault_codes),
    resolutionSummary: (summary && (onJob ? summary.completion_summary : summary.resolution_summary)) || '',
    rootCause: (summary && summary.root_cause) || '',
    startedAt: log.started_at ? toLocalDateTimeInput(log.started_at) : '',
    endedAt: log.ended_at ? toLocalDateTimeInput(log.ended_at) : ''
  }).current;

  const [notes, setNotes] = useState(initial.notes);
  const [faultCodes, setFaultCodes] = useState(initial.faultCodes);
  const [resolutionSummary, setResolutionSummary] = useState(initial.resolutionSummary);
  const [rootCause, setRootCause] = useState(initial.rootCause);
  const [startedAt, setStartedAt] = useState(initial.startedAt);
  const [endedAt, setEndedAt] = useState(initial.endedAt);
  const [photos, setPhotos] = useState([]);
  // check_id -> { status, form, answers, startAnswers, stepPhotos }
  const [openForms, setOpenForms] = useState({});
  const [spareLines, setSpareLines] = useState([]);
  const spareTextProblems = useRef({});
  // Whether pay lets the times be changed: read when the window opens.
  const [timesLocked, setTimesLocked] = useState({ status: 'loading', sentence: null });
  // The log's own journey, for site work: 'loading', 'none', or the record.
  const [journey, setJourney] = useState(isSiteWork && mine ? 'loading' : 'none');
  // A Job Stages time set in this window is saved there and then; the work
  // log's own times follow on the next save, so that save has something to do.
  const [journeyChanged, setJourneyChanged] = useState(false);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await send(authFetch, `${basePath}/changes`, { method: 'GET' });
        if (!cancelled) setTimesLocked({ status: 'ready', sentence: data.times_locked || null });
      } catch (err) {
        // Not known: the boxes stay usable and the server decides on save.
        if (!cancelled) setTimesLocked({ status: 'ready', sentence: null });
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, basePath]);

  useEffect(() => {
    if (!isSiteWork || !mine) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch(`/api/v1/travel/stages/current?work_log_id=${encodeURIComponent(log.log_id)}`);
        const data = await response.json().catch(() => ({}));
        if (!cancelled) setJourney(response.ok && data.data ? data.data : 'none');
      } catch (err) {
        if (!cancelled) setJourney('none');
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, isSiteWork, mine, log.log_id]);

  // A refusal shows at the top of a long window, and the buttons are at the bottom.
  useEffect(() => {
    if (error && bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [error]);

  const addPhotos = async (files, input) => {
    setError(null);
    try {
      const picked = await filesToAttachmentPayload(files);
      setPhotos(previous => [...previous, ...picked]);
    } catch (err) {
      setError(err.message);
    } finally {
      if (input) input.value = '';
    }
  };

  const openForm = async (checkId) => {
    setOpenForms(previous => ({ ...previous, [checkId]: { status: 'loading' } }));
    try {
      const form = await send(authFetch, `/api/v1/work-logs/forms/${encodeURIComponent(checkId)}`, { method: 'GET' });
      const answers = form.template_answers && typeof form.template_answers === 'object' ? form.template_answers : {};
      setOpenForms(previous => ({
        ...previous,
        [checkId]: { status: 'ready', form, answers, startAnswers: JSON.stringify(answers), stepPhotos: storedStepPhotos(form.attachments) }
      }));
    } catch (err) {
      setOpenForms(previous => ({ ...previous, [checkId]: { status: 'failed', message: err.message } }));
    }
  };

  const changeForm = (checkId, change) => setOpenForms(previous => ({
    ...previous,
    [checkId]: { ...previous[checkId], ...change(previous[checkId]) }
  }));

  const addFormPhotos = async (checkId, stepKey, files, input) => {
    setError(null);
    try {
      const picked = (await filesToAttachmentPayload(files))
        .map(photo => ({ ...photo, step_key: stepKey, filename: photo.name, data: photo.dataUrl }));
      changeForm(checkId, held => ({
        stepPhotos: { ...held.stepPhotos, [stepKey]: [...(held.stepPhotos[stepKey] || []), ...picked] }
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      if (input) input.value = '';
    }
  };

  const removeFormPhoto = (checkId, stepKey, index) => {
    const held = openForms[checkId];
    const photo = held && held.stepPhotos[stepKey] ? held.stepPhotos[stepKey][index] : null;
    if (!photo) return;
    if (photo.stored) {
      setError(STORED_PHOTO_MESSAGE);
      return;
    }
    changeForm(checkId, current => ({
      stepPhotos: { ...current.stepPhotos, [stepKey]: current.stepPhotos[stepKey].filter((item, at) => at !== index) }
    }));
  };

  const addSpareLine = (picked) => setSpareLines(lines => [...lines, {
    key: newUuid(),
    value: {
      catalogue_id: picked.notInList ? null : picked.id,
      item_number: picked.notInList ? null : (picked.number || null),
      name: picked.name,
      quantity: 1,
      asset_id: null,
      whole_job: false,
      not_in_list: Boolean(picked.notInList),
      added_by: (user && user.userId) || null,
      added_at: new Date().toISOString()
    }
  }]);

  /** What this save would send, or a sentence saying why it cannot be sent. */
  const buildBody = () => {
    const body = {};
    if (notes !== initial.notes) body.notes = notes;
    if (!onJob && faultCodes !== initial.faultCodes) body.fault_codes = parseFaultCodes(faultCodes);
    if (closedTheWork && onJob) {
      if (resolutionSummary !== initial.resolutionSummary) {
        if (!resolutionSummary.trim()) return { problem: JOB_SUMMARY_EMPTY_MESSAGE };
        body.completion_summary = resolutionSummary;
      }
    } else if (closedTheWork) {
      if (resolutionSummary !== initial.resolutionSummary) {
        if (!resolutionSummary.trim()) return { problem: SUMMARY_EMPTY_MESSAGE };
        body.resolution_summary = resolutionSummary;
      }
      if (rootCause !== initial.rootCause) body.root_cause = rootCause;
    }
    // Only a time that was changed is sent: the boxes hold whole minutes, and
    // an untouched one would otherwise read as a change of hours.
    if (!isSiteWork && (startedAt !== initial.startedAt || endedAt !== initial.endedAt)) {
      const start = new Date(startedAt);
      const finish = new Date(endedAt);
      if (!startedAt || !endedAt || Number.isNaN(start.getTime()) || Number.isNaN(finish.getTime())) {
        return { problem: 'Enter the start and finish as a date and time.' };
      }
      if (finish <= start) return { problem: FINISH_ORDER_MESSAGE };
      if (startedAt !== initial.startedAt) body.started_at = start.toISOString();
      if (endedAt !== initial.endedAt) body.ended_at = finish.toISOString();
    }
    if (photos.length > 0) body.attachments = photos;

    const formBodies = [];
    const formPhotos = [];
    Object.entries(openForms).forEach(([checkId, held]) => {
      if (!held || held.status !== 'ready') return;
      const every = Object.values(held.stepPhotos).flat();
      const added = every.filter(photo => !photo.stored);
      const answersChanged = JSON.stringify(held.answers) !== held.startAnswers;
      if (!answersChanged && added.length === 0) return;
      const entry = { check_id: checkId };
      if (answersChanged) entry.template_answers = held.answers;
      if (added.length > 0) {
        // Every photo already on the form is named, so the server knows none is being removed.
        const kept = (Array.isArray(held.form.attachments) ? held.form.attachments : [])
          .filter(photo => photo && photo.url).map(photo => ({ url: photo.url }));
        entry.attachments = [
          ...kept,
          ...added.map(photo => ({ name: photo.name, size: photo.size, type: photo.type, dataUrl: photo.dataUrl, step_key: photo.step_key }))
        ];
        formPhotos.push(...added);
      }
      formBodies.push(entry);
    });
    if (formBodies.length > 0) body.forms = formBodies;
    if (newPhotoBytes([...photos, ...formPhotos]) > ONE_SAVE_BUDGET_BYTES) return { problem: TOO_MANY_PHOTOS_MESSAGE };

    if (spareLines.length > 0) {
      const typed = Object.values(spareTextProblems.current).find(Boolean);
      // On a job with several robots each part says which one, or the whole job.
      const robotIds = new Set(onJob ? jobRobots.map(robot => String(robot.asset_id).toLowerCase()) : []);
      const problem = typed || firstSpareProblem(spareLines.map(line => line.value), { robotIds, severalRobots: robotIds.size > 1 });
      if (problem) return { problem };
      body.parts = spareLines.map(({ key, value }) => ({
        line_id: key,
        catalogue_id: value.catalogue_id,
        item_number: value.item_number,
        name: value.name,
        quantity: Number(value.quantity),
        not_in_list: value.not_in_list,
        ...(onJob ? { asset_id: value.asset_id || null, whole_job: value.whole_job === true } : {})
      }));
    }
    return { body };
  };

  const save = async ({ complete }) => {
    setError(null);
    const built = buildBody();
    if (built.problem) {
      setError(built.problem);
      return;
    }
    const body = built.body;
    if (!complete && Object.keys(body).length === 0 && !journeyChanged) {
      setError(NOTHING_CHANGED_MESSAGE);
      return;
    }
    if (complete) body.complete = true;
    setSaving(complete ? 'complete' : 'save');
    try {
      const data = await send(authFetch, `${basePath}/update`, { method: 'PATCH', body });
      onSaved({
        completed: Boolean(complete),
        changed: Array.isArray(data.changed) ? data.changed : [],
        payrollNotice: data.payroll_notice || null,
        alreadyRecorded: Array.isArray(data.spare_parts_already_recorded) ? data.spare_parts_already_recorded : []
      });
    } catch (err) {
      setError(err.message);
      setSaving(null);
    }
  };

  const busy = saving !== null;
  const editableForms = (Array.isArray(forms) ? forms : []).filter(form => form && form.check_id && !form.is_van_check);
  const existingPhotos = Array.isArray(log.attachments) ? log.attachments : [];
  const timesBusy = timesLocked.status === 'loading';

  return (
    <div className="modal-overlay">
      <div
        className="modal-content work-log-update-window"
        role="dialog"
        aria-modal="true"
        aria-label="Update work log"
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Update work log</h2>
          <button type="button" className="modal-close" aria-label="Close" onClick={onClose} disabled={busy}>&times;</button>
        </div>
        <div className="modal-body" ref={bodyRef}>
          {error && <div className="modal-error" role="alert">{error}</div>}
          <div className="modal-hint">
            {words(log.log_type)} by {log.engineer_name || 'the engineer'}, {formatDateTime(log.started_at)},
            completed as <strong>{words(log.outcome)}</strong>.
            {reopen.reopen_reason ? ` Reopened${reopen.reopened_by_name ? ` by ${reopen.reopened_by_name}` : ''}: ${reopen.reopen_reason}` : ''}
            <br />
            The outcome, work type and crew stay as they are. Everything changed here is recorded under the work log.
          </div>

          <div className="form-group">
            <label htmlFor="work_log_update_notes">Notes</label>
            <textarea
              id="work_log_update_notes"
              rows={5}
              value={notes}
              onChange={event => setNotes(event.target.value)}
              disabled={busy}
            />
          </div>

          {!onJob && (
            <div className="form-group">
              <label htmlFor="work_log_update_fault_codes">Fault codes</label>
              <input
                type="text"
                id="work_log_update_fault_codes"
                value={faultCodes}
                onChange={event => setFaultCodes(event.target.value)}
                placeholder="e.g. E104, E220"
                disabled={busy}
              />
              <span className="field-help">Separate several codes with commas.</span>
            </div>
          )}

          {closedTheWork && onJob && (
            <div className="form-group">
              <label htmlFor="work_log_update_summary">Completion summary *</label>
              <textarea
                id="work_log_update_summary"
                rows={3}
                value={resolutionSummary}
                onChange={event => setResolutionSummary(event.target.value)}
                disabled={busy}
              />
              <span className="field-help">What the job's record shows as the outcome of this visit.</span>
            </div>
          )}

          {closedTheWork && !onJob && (
            <>
              <div className="form-group">
                <label htmlFor="work_log_update_summary">Resolution summary *</label>
                <textarea
                  id="work_log_update_summary"
                  rows={3}
                  value={resolutionSummary}
                  onChange={event => setResolutionSummary(event.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="form-group">
                <label htmlFor="work_log_update_root_cause">Root cause</label>
                <textarea
                  id="work_log_update_root_cause"
                  rows={2}
                  value={rootCause}
                  onChange={event => setRootCause(event.target.value)}
                  disabled={busy}
                />
              </div>
            </>
          )}

          <div className="work-log-update-section">
            <h3>Times</h3>
            {timesLocked.sentence ? (
              <p className="modal-hint warning" role="status">{timesLocked.sentence}</p>
            ) : isSiteWork ? (
              <>
                {!mine && (
                  <p className="field-help">
                    Site work takes its times from Job Stages. Only {log.engineer_name || 'the engineer'} can correct them, from this window.
                  </p>
                )}
                {mine && journey === 'loading' && <p className="field-help" role="status">Reading the Job Stages times...</p>}
                {mine && journey === 'none' && (
                  <p className="field-help">This work log has no Job Stages record, so its times cannot be corrected here. Ask a manager.</p>
                )}
                {mine && journey && typeof journey === 'object' && (
                  <JobStagesCard
                    authFetch={authFetch}
                    ticketId={onJob ? null : ticketId}
                    taskId={onJob ? taskId : null}
                    workLogId={log.log_id}
                    travelRecord={journey}
                    onTravelRecordChange={(record) => { setJourney(record); setJourneyChanged(true); }}
                    skipLoad
                    manualOnly
                    sectionTitle="Job Stages"
                    sectionHint={SITE_TIMES_HINT}
                  />
                )}
              </>
            ) : (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="work_log_update_started">Started</label>
                  <input
                    type="datetime-local"
                    id="work_log_update_started"
                    value={startedAt}
                    onChange={event => setStartedAt(event.target.value)}
                    disabled={busy || timesBusy}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="work_log_update_finished">Finished</label>
                  <input
                    type="datetime-local"
                    id="work_log_update_finished"
                    value={endedAt}
                    onChange={event => setEndedAt(event.target.value)}
                    disabled={busy || timesBusy}
                  />
                </div>
              </div>
            )}
            {!timesLocked.sentence && !isSiteWork && (
              <span className="field-help">Leave the times alone unless they were wrong: a change of times changes the hours.</span>
            )}
          </div>

          {editableForms.length > 0 && (
            <div className="work-log-update-section">
              <h3>Forms filled in on this work log</h3>
              {editableForms.map(form => {
                const held = openForms[form.check_id];
                return (
                  <div key={form.check_id} className="work-log-update-form">
                    <div className="work-log-update-form-head">
                      <span>{formLine(form)}</span>
                      {(!held || held.status === 'failed') && (
                        <button type="button" className="btn-small" disabled={busy} onClick={() => openForm(form.check_id)}>
                          Change answers
                        </button>
                      )}
                    </div>
                    {held && held.status === 'loading' && <p className="field-help" role="status">Opening the form...</p>}
                    {held && held.status === 'failed' && <p className="modal-error" role="alert">{held.message}</p>}
                    {held && held.status === 'ready' && !hasSections(held.form.template_snapshot) && (
                      <p className="field-help">This form cannot be changed here.</p>
                    )}
                    {held && held.status === 'ready' && hasSections(held.form.template_snapshot) && (
                      <TemplateCheckFormRenderer
                        schema={held.form.template_snapshot}
                        answers={held.answers}
                        onAnswersChange={answers => changeForm(form.check_id, () => ({ answers }))}
                        disabled={busy}
                        stepAttachments={held.stepPhotos}
                        onAddPhotos={(stepKey, files, input) => addFormPhotos(form.check_id, stepKey, files, input)}
                        onRemovePhoto={(stepKey, index) => removeFormPhoto(form.check_id, stepKey, index)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="work-log-update-section">
            <h3>Photos</h3>
            {existingPhotos.length > 0 && (
              <div className="attachment-list compact">
                {existingPhotos.map((photo, index) => (photo.url ? (
                  <a
                    key={photo.attachment_id || index}
                    href={authedFileHref(photo.url)}
                    target="_blank"
                    rel="noreferrer"
                    className="attachment-chip"
                  >
                    {photo.name || `Attachment ${index + 1}`}
                  </a>
                ) : (
                  <span key={photo.attachment_id || index} className="attachment-chip muted">{photo.name || `Attachment ${index + 1}`}</span>
                )))}
              </div>
            )}
            {photos.length > 0 && (
              <ul className="work-log-update-new-photos" aria-label="Photos to add">
                {photos.map((photo, index) => (
                  <li key={`${photo.name}-${index}`}>
                    {photo.name}
                    <button
                      type="button"
                      className="btn-small"
                      aria-label={`Remove ${photo.name}`}
                      disabled={busy}
                      onClick={() => setPhotos(previous => previous.filter((item, at) => at !== index))}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <label className="work-log-update-add-photos">
              <span className="btn-small">Add photos or files</span>
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.txt"
                aria-label="Add photos or files to this work log"
                disabled={busy}
                onChange={event => addPhotos(event.target.files, event.target)}
              />
            </label>
            <span className="field-help">What is already on the work log stays on it.</span>
          </div>

          {catalogue && (
            <div className="work-log-update-section">
              <h3>Parts missed</h3>
              {recordedParts && <p className="field-help">Already recorded on this work log: {recordedParts}</p>}
              <SparePartsUsed
                id="work-log-update-parts"
                lines={spareLines}
                robots={onJob ? jobRobots : []}
                catalogue={catalogue}
                showAddedBy={false}
                disabled={busy}
                onAdd={addSpareLine}
                onChange={(key, value) => setSpareLines(lines => lines.map(line => (line.key === key ? { ...line, value } : line)))}
                onRemove={key => setSpareLines(lines => lines.filter(line => line.key !== key))}
                onQuantityProblem={(key, sentence) => { spareTextProblems.current[key] = sentence || undefined; }}
              />
              <span className="field-help">
                A part added here is dated to the visit. A part already recorded is corrected in Parts used, by a team leader or manager.
              </span>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-cancel" disabled={busy} onClick={onClose}>Close</button>
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => save({ complete: false })}>
            {saving === 'save' ? 'Saving...' : 'Save Progress'}
          </button>
          <button type="button" className="btn-save" disabled={busy} onClick={() => save({ complete: true })}>
            {saving === 'complete' ? 'Saving...' : 'Complete Work Log'}
          </button>
        </div>
      </div>
    </div>
  );
}
